import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';
import { api } from '../api';
import { extractYouTubeData } from '../youtube';

function ArticleAdmin() {
  const [articles, setArticles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [status, setStatus] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    coverImage: '',
    author: 'William',
    tags: '',
    published: true,
    youtubeUrl: '',
    publishDate: '',
    hideYoutube: false
  });

  // AI Generation state
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationInfo, setGenerationInfo] = useState(null);
  const [showTranscriptPaste, setShowTranscriptPaste] = useState(false);
  const [manualTranscript, setManualTranscript] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-6');

  // Style rules state
  const [styleRules, setStyleRules] = useState('');
  const [showStyleRules, setShowStyleRules] = useState(false);
  const [styleRulesSaved, setStyleRulesSaved] = useState(false);

  // YouTube feed state
  const [latestVideos, setLatestVideos] = useState([]);
  const [generatingVideoId, setGeneratingVideoId] = useState(null);

  useEffect(() => {
    fetchArticles();
    fetchStyleRules();
    fetchLatestVideos();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await api('/api/articles');
      if (res.ok) {
        const data = await res.json();
        setArticles(data);
      }
    } catch (err) {
      console.error('Kunne ikke hente artikler:', err);
    }
  };

  const fetchStyleRules = async () => {
    try {
      const res = await api('/api/style-rules');
      if (res.ok) {
        const data = await res.json();
        setStyleRules(data.rules || '');
      }
    } catch (err) {
      // Style rules not available yet
    }
  };

  const fetchLatestVideos = async () => {
    try {
      const res = await api('/api/youtube-latest');
      if (res.ok) {
        const data = await res.json();
        setLatestVideos(data);
      }
    } catch (err) {
      console.error('Kunne ikke hente YouTube-videoer:', err);
    }
  };

  const generateFromVideo = async (video) => {
    setYoutubeUrl(video.url);
    setGeneratingVideoId(video.videoId);
    setGenerating(true);
    setStatus(null);
    setGenerationInfo(null);

    try {
      setStatus({ type: 'info', message: 'Henter data fra YouTube...' });
      let clientVideoData = null;
      try {
        clientVideoData = await extractYouTubeData(video.url);
      } catch {}

      // Pre-fill publish date and cover image
      setFormData(prev => ({
        ...prev,
        publishDate: clientVideoData?.publishDate || video.published || prev.publishDate,
        coverImage: `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`,
        youtubeUrl: video.url
      }));

      if (!clientVideoData?.transcript) {
        setStatus({ type: 'error', message: 'Kunne ikke hente transkripsjon automatisk. Lim inn transkripsjonen manuelt nedenfor.' });
        setShowTranscriptPaste(true);
        setGenerating(false);
        setGeneratingVideoId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setStatus({ type: 'info', message: 'Genererer artikkel med AI...' });
      const res = await sendToGenerate(video.url, clientVideoData);
      const data = await res.json();

      if (res.ok && data.success) {
        setFormData(prev => ({
          ...prev,
          title: data.article.title,
          excerpt: data.article.excerpt,
          content: data.article.content,
          coverImage: data.article.coverImage || '',
          tags: data.article.tags?.slice(0, 3).join(', ') || '',
          youtubeUrl: video.url,
          publishDate: data.videoData.publishDate || clientVideoData?.publishDate || video.published || ''
        }));
        setGenerationInfo({ videoTitle: data.videoData.title, hasTranscript: true });
        setStatus({ type: 'success', message: 'Artikkel generert! Gjør endringer og publiser.' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setStatus({ type: 'error', message: data.error || 'Kunne ikke generere artikkel' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke koble til serveren.' });
    } finally {
      setGenerating(false);
      setGeneratingVideoId(null);
    }
  };

  const saveStyleRules = async () => {
    try {
      const res = await api('/api/style-rules', {
        method: 'PUT',
        body: JSON.stringify({ rules: styleRules })
      });
      if (res.ok) {
        setStyleRulesSaved(true);
        setTimeout(() => setStyleRulesSaved(false), 2000);
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke lagre skriveregler' });
    }
  };

  const sendToGenerate = async (url, clientVideoData) => {
    const res = await api('/api/generate-article', {
      method: 'POST',
      body: JSON.stringify({ youtubeUrl: url, videoData: clientVideoData, model: selectedModel })
    });
    return res;
  };

  const generateWithTranscript = async (url, transcript, publishDate) => {
    const videoId = url.match(/(?:v=|youtu\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/)?.[1];
    const clientVideoData = {
      videoId,
      title: '',
      description: '',
      transcript: transcript.substring(0, 50000),
      publishDate: publishDate || ''
    };

    setGenerating(true);
    setStatus({ type: 'info', message: 'Genererer artikkel med AI... Dette kan ta opptil 30 sekunder.' });

    try {
      const res = await sendToGenerate(url, clientVideoData);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setStatus({ type: 'error', message: errData.error || `Serverfeil (${res.status}). Prøv igjen.` });
        return;
      }
      const data = await res.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          title: data.article.title,
          excerpt: data.article.excerpt,
          content: data.article.content,
          coverImage: data.article.coverImage || '',
          tags: data.article.tags?.slice(0, 3).join(', ') || '',
          youtubeUrl: url,
          publishDate: data.videoData.publishDate || publishDate || ''
        }));
        setGenerationInfo({ videoTitle: data.videoData.title, hasTranscript: true });
        setStatus({ type: 'success', message: 'Artikkel generert! Gjør endringer og publiser.' });
        setShowTranscriptPaste(false);
        setManualTranscript('');
      } else {
        setStatus({ type: 'error', message: data.error || 'Kunne ikke generere artikkel' });
      }
    } catch (err) {
      console.error('Generate error:', err);
      setStatus({ type: 'error', message: `Nettverksfeil: ${err.message}. Sjekk at backend kjører.` });
    } finally {
      setGenerating(false);
      setGeneratingVideoId(null);
    }
  };

  const generateArticle = async () => {
    if (!youtubeUrl.trim()) return;

    // If manual transcript is provided, use it directly
    if (manualTranscript.trim()) {
      return generateWithTranscript(youtubeUrl.trim(), manualTranscript.trim());
    }

    setGenerating(true);
    setStatus(null);
    setGenerationInfo(null);

    try {
      // Try extracting transcript automatically
      setStatus({ type: 'info', message: 'Henter data fra YouTube...' });
      let clientVideoData = null;
      try {
        clientVideoData = await extractYouTubeData(youtubeUrl.trim());
      } catch {}

      // Pre-fill publish date and cover image even if transcript fails
      if (clientVideoData) {
        const vid = clientVideoData.videoId;
        setFormData(prev => ({
          ...prev,
          publishDate: clientVideoData.publishDate || prev.publishDate,
          coverImage: vid ? `https://img.youtube.com/vi/${vid}/maxresdefault.jpg` : prev.coverImage,
          youtubeUrl: youtubeUrl.trim()
        }));
      }

      if (!clientVideoData?.transcript) {
        setStatus({ type: 'error', message: 'Kunne ikke hente transkripsjon automatisk. Lim inn transkripsjonen manuelt nedenfor.' });
        setShowTranscriptPaste(true);
        setGenerating(false);
        return;
      }

      setStatus({ type: 'info', message: 'Genererer artikkel med AI...' });
      const res = await sendToGenerate(youtubeUrl.trim(), clientVideoData);
      const data = await res.json();

      if (res.ok && data.success) {
        setFormData(prev => ({
          ...prev,
          title: data.article.title,
          excerpt: data.article.excerpt,
          content: data.article.content,
          coverImage: data.article.coverImage || '',
          tags: data.article.tags?.slice(0, 3).join(', ') || '',
          youtubeUrl: youtubeUrl.trim(),
          publishDate: data.videoData.publishDate || clientVideoData?.publishDate || ''
        }));
        setGenerationInfo({
          videoTitle: data.videoData.title,
          hasTranscript: data.videoData.hasTranscript
        });
        setStatus({ type: 'success', message: 'Artikkel generert! Gjør endringer og publiser.' });
      } else {
        setStatus({ type: 'error', message: data.error || 'Kunne ikke generere artikkel' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke koble til serveren.' });
    } finally {
      setGenerating(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[æ]/g, 'ae')
      .replace(/[ø]/g, 'o')
      .replace(/[å]/g, 'a')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const articleData = {
      title: formData.title,
      excerpt: formData.excerpt,
      content: formData.content,
      coverImage: formData.coverImage,
      author: formData.author,
      youtubeUrl: formData.youtubeUrl,
      hideYoutube: formData.hideYoutube,
      published: formData.published,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      slug: generateSlug(formData.title),
      publishDate: formData.publishDate || null
    };

    try {
      const endpoint = editingId ? `/api/articles/${editingId}` : '/api/articles';
      const method = editingId ? 'PUT' : 'POST';

      const res = await api(endpoint, {
        method,
        body: JSON.stringify(articleData)
      });

      if (res.ok) {
        setStatus({ type: 'success', message: editingId ? 'Artikkel oppdatert!' : 'Artikkel publisert!' });
        resetForm();
        fetchArticles();
      } else {
        const err = await res.json();
        setStatus({ type: 'error', message: err.error || 'Noe gikk galt' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke koble til serveren. Er backend startet?' });
    }
  };

  const handleEdit = (article) => {
    setEditingId(article.id);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      coverImage: article.coverImage || '',
      author: article.author,
      tags: article.tags?.join(', ') || '',
      published: article.published,
      youtubeUrl: article.youtubeUrl || '',
      publishDate: article.createdAt || '',
      hideYoutube: article.hideYoutube || false
    });
    setShowPreview(false);
    setGenerationInfo(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker pa at du vil slette denne artikkelen?')) return;

    try {
      const res = await api(`/api/articles/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setStatus({ type: 'success', message: 'Artikkel slettet!' });
        fetchArticles();
        if (editingId === id) resetForm();
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke slette artikkelen' });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      coverImage: '',
      author: 'William',
      tags: '',
      published: true
    });
    setShowPreview(false);
    setGenerationInfo(null);
    setYoutubeUrl('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const inputClass = "w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-gray-200 placeholder-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors";
  const labelClass = "block text-sm font-medium text-gray-300 mb-1.5";

  return (
    <div className="min-h-screen bg-slate-900 pt-8 pb-16 px-4">
      <SEO title="Artikkel-admin" path="/artikler/admin" noindex />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Artikkel Admin</h1>
          <div className="flex gap-3">
            <Link
              to="/admin/meny"
              className="px-4 py-2 bg-slate-800 text-gray-300 rounded-xl hover:bg-slate-700 transition-colors text-sm"
            >
              Admin-meny
            </Link>
            <Link
              to="/artikler"
              className="px-4 py-2 bg-slate-800 text-gray-300 rounded-xl hover:bg-slate-700 transition-colors text-sm"
            >
              Se artikler
            </Link>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
        {/* Main column */}
        <div className="flex-1 min-w-0">

        {/* Status message */}
        {status && (
          <div className={`mb-6 p-4 rounded-xl text-sm font-medium ${
            status.type === 'success' ? 'bg-emerald-500 bg-opacity-20 text-emerald-400 border border-emerald-500 border-opacity-30' : status.type === 'info' ? 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-30' : 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30'
          }`}>
            {status.message}
          </div>
        )}

        {/* AI Generation Section */}
        <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-500 border-opacity-30 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h2 className="text-xl font-bold text-white">Generer med AI</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Lim inn en YouTube-URL, sa skriver Claude en komplett artikkel basert pa videoen.
          </p>

          <div className="flex gap-2 mb-3">
            {[
              { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', desc: 'Raskere · billigere' },
              { id: 'claude-opus-4-8', label: 'Opus 4.8', desc: 'Smartere · tregere' }
            ].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedModel(m.id)}
                disabled={generating}
                className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors disabled:opacity-50 ${
                  selectedModel === m.id
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : 'bg-slate-800 border-slate-600 text-gray-400 hover:border-purple-500 hover:text-gray-200'
                }`}
              >
                <div>{m.label}</div>
                <div className="text-xs opacity-70 font-normal">{m.desc}</div>
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <input
              type="url"
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className={inputClass + ' flex-1'}
              disabled={generating}
            />
            <button
              type="button"
              onClick={generateArticle}
              disabled={generating || !youtubeUrl.trim()}
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 flex-shrink-0"
            >
              {generating ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Genererer...
                </>
              ) : 'Generer'}
            </button>
          </div>

          {generationInfo && (
            <div className="mt-3 text-sm text-purple-300">
              Video: {generationInfo.videoTitle}
              {generationInfo.hasTranscript && (
                <span className="ml-2 px-2 py-0.5 bg-purple-500 bg-opacity-20 rounded-full text-xs">
                  Med transkripsjon
                </span>
              )}
            </div>
          )}

          {/* Manual transcript paste fallback */}
          {showTranscriptPaste && (
            <div className="mt-4 p-4 bg-slate-800 rounded-xl border border-yellow-500 border-opacity-30">
              <p className="text-yellow-400 text-sm font-medium mb-2">
                YouTube blokkerer automatisk henting. Lim inn transkripsjonen manuelt:
              </p>
              <p className="text-gray-500 text-xs mb-3">
                Gå til videoen på YouTube → klikk "..." under videoen → "Vis transkripsjon" → marker alt → kopier → lim inn her.
              </p>
              <textarea
                value={manualTranscript}
                onChange={e => setManualTranscript(e.target.value)}
                placeholder="Lim inn transkripsjon her..."
                rows={6}
                className={inputClass + ' resize-y text-sm mb-3'}
              />
              <button
                type="button"
                onClick={() => generateWithTranscript(youtubeUrl.trim(), manualTranscript.trim())}
                disabled={!manualTranscript.trim() || generating}
                className="px-5 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Generer med limt transkripsjon
              </button>
            </div>
          )}

          {/* Style Rules Toggle */}
          <div className="mt-4 pt-4 border-t border-purple-500 border-opacity-20">
            <button
              type="button"
              onClick={() => setShowStyleRules(!showStyleRules)}
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2"
            >
              <svg className={`w-4 h-4 transition-transform ${showStyleRules ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Skriveregler og stil
            </button>

            {showStyleRules && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-gray-500">
                  Definer hvordan Claude skal skrive artiklene dine. Disse reglene brukes ved hver generering.
                </p>
                <textarea
                  value={styleRules}
                  onChange={e => setStyleRules(e.target.value)}
                  placeholder={"Eksempler pa skriveregler:\n\n- Skriv i en uformell og engasjerende tone\n- Bruk korte avsnitt, maks 3-4 setninger\n- Inkluder alltid en konklusjon med \"Vart inntrykk\"\n- Unnga teknisk sjargong uten forklaring\n- Bruk aktiv stemme\n- Referer til videoen naturlig i teksten\n- Inkluder relevante emoji i overskrifter"}
                  rows={8}
                  className={inputClass + ' resize-y font-mono text-sm'}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={saveStyleRules}
                    className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-500 transition-colors text-sm"
                  >
                    Lagre regler
                  </button>
                  {styleRulesSaved && (
                    <span className="text-sm text-emerald-400">Lagret!</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-2xl p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-white mb-6">
            {editingId ? 'Rediger artikkel' : 'Ny artikkel'}
          </h2>

          <div className="space-y-5">
            <div>
              <label className={labelClass}>Tittel *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Artikkeltittel"
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Forfatter *</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Navn pa forfatter"
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Kort beskrivelse *</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="En kort oppsummering som vises i artikkellisten"
                required
                rows={2}
                className={inputClass + ' resize-none'}
              />
            </div>

            <div>
              <label className={labelClass}>Coverbilde URL</label>
              <input
                type="url"
                name="coverImage"
                value={formData.coverImage}
                onChange={handleChange}
                placeholder="https://example.com/bilde.jpg"
                className={inputClass}
              />
              {formData.coverImage && (
                <img
                  src={formData.coverImage}
                  alt="Forhandsvisning"
                  className="mt-2 w-full max-h-48 object-cover rounded-lg"
                  onError={e => e.target.style.display = 'none'}
                />
              )}
            </div>

            <div>
              <label className={labelClass}>Tags (kommaseparert)</label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="elbil, test, lading"
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Publiseringsdato</label>
                <input
                  type="date"
                  name="publishDate"
                  value={formData.publishDate}
                  onChange={handleChange}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-gray-500">Settes automatisk fra YouTube-video</p>
              </div>
              <div>
                <label className={labelClass}>YouTube-video URL</label>
                <input
                  type="url"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleChange}
                  placeholder="https://youtube.com/watch?v=..."
                  className={inputClass}
                />
              </div>
            </div>


            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-300">Innhold (Markdown) *</label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                >
                  {showPreview ? 'Rediger' : 'Forhandsvisning'}
                </button>
              </div>

              {showPreview ? (
                <div className="min-h-[300px] px-5 py-4 bg-slate-700 border border-slate-600 rounded-xl text-gray-200 prose-article">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {formData.content || '*Ingen innhold enna...*'}
                  </ReactMarkdown>
                </div>
              ) : (
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder={"# Overskrift\n\nSkriv artikkelen din her med **Markdown**...\n\n## Underoverskrift\n\n- Punkt 1\n- Punkt 2\n\n> Sitat"}
                  required
                  rows={16}
                  className={inputClass + ' resize-y font-mono text-sm'}
                />
              )}

              <p className="mt-2 text-xs text-gray-500">
                Stotter Markdown: # Overskrifter, **fet**, *kursiv*, - lister, &gt; sitat, [lenker](url), ![bilder](url), tabeller, og mer.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                name="published"
                checked={formData.published}
                onChange={handleChange}
                className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-teal-500 focus:ring-teal-500"
              />
              <label htmlFor="published" className="text-sm text-gray-300">
                Publisert (synlig for lesere)
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hideYoutube"
                name="hideYoutube"
                checked={formData.hideYoutube}
                onChange={handleChange}
                className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-teal-500 focus:ring-teal-500"
              />
              <label htmlFor="hideYoutube" className="text-sm text-gray-300">
                Skjul YouTube-lenker i artikkelen
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button
              type="submit"
              className="px-6 py-3 bg-teal-500 text-slate-900 font-bold rounded-xl hover:bg-teal-400 transition-colors"
            >
              {editingId ? 'Oppdater artikkel' : 'Publiser artikkel'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-slate-700 text-gray-300 font-bold rounded-xl hover:bg-slate-600 transition-colors"
              >
                Avbryt
              </button>
            )}
          </div>
        </form>

        {/* Existing articles */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Eksisterende artikler ({articles.length})</h2>
          <div className="space-y-3">
            {articles.map(article => (
              <div
                key={article.id}
                className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-semibold truncate">{article.title}</h3>
                    {!article.published && (
                      <span className="px-2 py-0.5 bg-yellow-500 bg-opacity-20 text-yellow-400 text-xs rounded-full flex-shrink-0">
                        Utkast
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{article.excerpt}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => {
                      const url = `https://effektnorge.no/artikler/${article.slug}`;
                      const tags = (article.tags || []).map(t => `#${t.replace(/\s+/g, '')}`).join(' ');
                      const text = `${article.excerpt} ${tags}`;
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    className="px-4 py-2 bg-blue-600 bg-opacity-20 text-blue-400 rounded-lg hover:bg-opacity-30 transition-colors text-sm"
                    title="Del på Facebook"
                  >
                    FB
                  </button>
                  <button
                    onClick={() => handleEdit(article)}
                    className="px-4 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                  >
                    Rediger
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    className="px-4 py-2 bg-red-500 bg-opacity-20 text-red-400 rounded-lg hover:bg-opacity-30 transition-colors text-sm"
                  >
                    Slett
                  </button>
                </div>
              </div>
            ))}

            {articles.length === 0 && (
              <p className="text-gray-500 text-center py-8">Ingen artikler enna. Opprett din forste ovenfor!</p>
            )}
          </div>
        </div>

        </div>{/* End main column */}

        {/* Sidebar - Latest YouTube videos */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="lg:sticky lg:top-8">
            <div className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814Z"/>
                  <path fill="#fff" d="m9.545 15.568 6.273-3.568-6.273-3.568v7.136Z"/>
                </svg>
                <h3 className="text-sm font-bold text-white">Siste videoer</h3>
              </div>

              <div className="space-y-3">
                {latestVideos.map(video => (
                  <div key={video.videoId} className="group">
                    <a href={video.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full rounded-lg mb-2 group-hover:opacity-80 transition-opacity"
                      />
                    </a>
                    <p className="text-xs text-gray-300 font-medium leading-tight mb-1.5 line-clamp-2">
                      {video.title}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{video.published}</span>
                      <button
                        onClick={() => generateFromVideo(video)}
                        disabled={generating}
                        className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        {generatingVideoId === video.videoId ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            ...
                          </>
                        ) : 'Generer'}
                      </button>
                    </div>
                  </div>
                ))}

                {latestVideos.length === 0 && (
                  <p className="text-gray-500 text-xs text-center py-4">Laster videoer...</p>
                )}
              </div>
            </div>
          </div>
        </div>

        </div>{/* End flex row */}
      </div>
    </div>
  );
}

export default ArticleAdmin;
